import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Profile = () => (
  <>
    <Card gap='$4'>
      <Paragraph size='$5'>
        Your profile is your public page on Guild. It shows who you are, which{' '}
        <Link href='/docs/guilds/overview'>Guilds</Link> you belong to, which{' '}
        <Link href='/docs/events/overview'>Events</Link> you have attended, and
        which{' '}
        <Link href='/docs/presentations/overview'>Presentations</Link> you have
        given. Anyone can visit your profile by navigating to your unique URL.
      </Paragraph>
      <DocScreenshot
        src='docs/account/user-profile-page.png'
        alt='User profile page showing profile photo, background photo, display name, bio, Guilds, Events, and Presentations sections'
        caption='Your profile page shows your Guilds, Events, and Presentations in one place'
      />
    </Card>

    <Card gap='$4'>
      <DocSection id='what-appears-on-your-profile' title='What Appears on Your Profile'>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='image' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Profile photo and background</Paragraph>{' '}
              — Your profile photo appears in search results, Member lists, and
              anywhere your name is displayed across Guild. The background photo adds
              a visual header to your profile page.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='person' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Display name and bio</Paragraph>{' '}
              — Your display name is how other Members see you throughout the
              platform. The bio gives you space to introduce yourself, share your
              interests, or describe what you do.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='people' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Your Guilds</Paragraph> — A list
              of every Guild you are a Member of. Visitors can see which communities
              you are part of and click through to explore them.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Events attended</Paragraph> —
              Events you have RSVPed to or checked into appear on your profile,
              giving a history of the gatherings you have been part of.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Presentations given</Paragraph>{' '}
              — Any Presentations linked to your account are displayed on your
              profile, creating a portfolio of your talks and recordings. See{' '}
              <Link href='/docs/presentations/claiming'>Claiming a Presentation</Link>{' '}
              to learn how Presentations are linked to your profile.
            </Paragraph>
          </XStack>
        </YStack>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='editing-your-profile' title='Editing Your Profile'>
        <Paragraph size='$5'>
          To edit your profile, click your profile photo in the top-right corner
          of the navigation bar and select{' '}
          <Paragraph size='$5' fontWeight='$7'>Settings</Paragraph>. From there
          you can update your display name, bio, profile photo, and background
          photo. Changes are saved immediately and reflected across the platform.
        </Paragraph>
        <Paragraph size='$5'>
          Your profile URL is based on your unique slug. You can change your slug
          from the Settings page, but keep in mind that changing it will update
          your profile URL and any existing links to your old URL will no longer
          work.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default Profile
